import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { CalendarSession, Course, CoursePayload, CourseRun, RunPayload } from './types'

export const courseKeys = {
  all: ['courses'] as const,
  list: (archived: boolean) => [...courseKeys.all, 'list', archived] as const,
  one: (id: number) => [...courseKeys.all, 'one', id] as const,
  runs: (filters: object) => [...courseKeys.all, 'runs', filters] as const,
  run: (id: number) => [...courseKeys.all, 'run', id] as const,
  calendar: (from: string, to: string) => [...courseKeys.all, 'calendar', from, to] as const,
  mine: () => [...courseKeys.all, 'mine'] as const,
  family: (studentId: number) => [...courseKeys.all, 'family', studentId] as const,
}

export const coursesQuery = (archived = false) =>
  queryOptions({
    queryKey: courseKeys.list(archived),
    queryFn: () => api.get<Course[]>('/courses', { params: archived ? { archived: 1 } : undefined }),
  })
export const courseQuery = (id: number) =>
  queryOptions({ queryKey: courseKeys.one(id), queryFn: () => api.get<Course>(`/courses/${id}`) })
export const runsQuery = (filters: { courseId?: number; status?: string } = {}) =>
  queryOptions({
    queryKey: courseKeys.runs(filters),
    queryFn: () => api.get<CourseRun[]>('/course-runs', { params: filters }),
  })
export const runQuery = (id: number) =>
  queryOptions({ queryKey: courseKeys.run(id), queryFn: () => api.get<CourseRun>(`/course-runs/${id}`) })
export const calendarQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: courseKeys.calendar(from, to),
    queryFn: () => api.get<CalendarSession[]>('/course-runs/calendar', { params: { from, to } }),
  })
export const myRunsQuery = () =>
  queryOptions({ queryKey: courseKeys.mine(), queryFn: () => api.get<CourseRun[]>('/me/course-runs') })
export const familyRunsQuery = (studentId: number) =>
  queryOptions({
    queryKey: courseKeys.family(studentId),
    queryFn: () => api.get<CourseRun[]>(`/me/family/students/${studentId}/course-runs`),
  })

function useCourseMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courseKeys.all })
      void qc.invalidateQueries({ queryKey: ['lms'] })
    },
  })
}

export const useSaveCourse = () =>
  useCourseMutation(({ id, ...body }: CoursePayload & { id?: number }) =>
    id ? api.put<Course>(`/courses/${id}`, body) : api.post<Course>('/courses', body),
  )
export const useArchiveCourse = () =>
  useCourseMutation(({ id, archived }: { id: number; archived: boolean }) =>
    api.post<Course>(`/courses/${id}/${archived ? 'archive' : 'restore'}`),
  )
export const useDeleteCourse = () => useCourseMutation((id: number) => api.delete(`/courses/${id}`))
export const useSaveRun = () =>
  useCourseMutation(({ id, ...body }: RunPayload & { id?: number }) =>
    id ? api.put<CourseRun>(`/course-runs/${id}`, body) : api.post<CourseRun>('/course-runs', body),
  )
export const useDeleteRun = () => useCourseMutation((id: number) => api.delete(`/course-runs/${id}`))
